import type { DragEndEvent, DragOverEvent } from "@dnd-kit/core";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 移動先の上でドロップ状態をこの時間だけ保ってから離したときにだけ移動を確定する。
 * ファイル一覧は行全体がドラッグ元なので、開く / プレビューするつもりの操作が
 * そのまま移動として確定してしまうのを防ぐための歯止め。
 */
export const DROP_DWELL_MS = 600;

/**
 * ドロップ先ごとの「もう離して良い」状態。isOver になってから DROP_DWELL_MS
 * 経過で true になる。見た目のフィードバック専用で、移動を確定するかの判定は
 * useDropDwellGate 側が持つ（setTimeout は遅れることがあり、見た目が先に
 * 確定可能になってしまわないようにするため）。
 */
export const useDropDwellReady = (isOver: boolean): boolean => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!isOver) return;
    const timer = window.setTimeout(() => setReady(true), DROP_DWELL_MS);
    return () => {
      window.clearTimeout(timer);
      setReady(false);
    };
  }, [isOver]);
  return ready;
};

/**
 * DndContext 側の滞留時間ゲート。onDragOver は移動先が変わったときだけ発火する
 * ので、そこで計測開始時刻を控えて onDragEnd で経過時間を見る。ドラッグ中に
 * 再レンダーを起こさないよう ref で持つ。
 */
export const useDropDwellGate = () => {
  const dwellRef = useRef<{ overId: string | null; since: number }>({
    overId: null,
    since: 0,
  });

  const resetDropDwell = useCallback(() => {
    dwellRef.current = { overId: null, since: 0 };
  }, []);

  const handleDropDwellOver = useCallback((event: DragOverEvent) => {
    dwellRef.current = {
      overId: event.over ? String(event.over.id) : null,
      since: Date.now(),
    };
  }, []);

  /** 移動先を十分な時間キープしたドロップか（false = 誤操作として無視する） */
  const isDropDwellSatisfied = useCallback((event: DragEndEvent): boolean => {
    const overId = event.over ? String(event.over.id) : null;
    if (overId === null) return false;
    const { overId: dwellOverId, since } = dwellRef.current;
    return dwellOverId === overId && Date.now() - since >= DROP_DWELL_MS;
  }, []);

  return {
    resetDropDwell,
    handleDropDwellOver,
    isDropDwellSatisfied,
  };
};

// 滞留中はグレー、移動できる状態になったら青。青は「離せば移動する」ことだけを
// 表す色として使い、それ以前の状態には一切使わない。
// --soremo-light-gray #a7a8a9
export const DROP_DWELL_FILL = "rgba(167, 168, 169, 0.20)";
export const DROP_DWELL_LINE = "rgba(167, 168, 169, 0.55)";
// --soremo-blue #009ace
export const DROP_READY_FILL = "rgba(0, 154, 206, 0.18)";

/**
 * 移動先の行に当てる見た目（td は透明なので tr の背景が行の状態色になる）。
 * 乗せている間はグレーが左から伸びていき、伸び切って移動できるようになった
 * ところで青に変わる。「まだ離しても移動しない」と「もう離せる」を、説明なしで
 * 色と面積だけで伝えるため。
 */
export const dropTargetRowStyle = ({
  isOver,
  isReady,
}: {
  isOver: boolean;
  isReady: boolean;
}): CSSProperties => {
  const fill = isReady ? DROP_READY_FILL : DROP_DWELL_FILL;
  return {
    backgroundImage: `linear-gradient(${fill}, ${fill})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: isOver ? "100% 100%" : "0 100%",
    // 伸びるのは滞留時間ぴったりで、引っ込むのは即座に（行を移った操作感のため）
    transition:
      isOver && !isReady
        ? `background-size ${DROP_DWELL_MS}ms linear`
        : "background-size 120ms ease-out",
    boxShadow: isReady
      ? "inset 0 0 0 2px var(--soremo-blue)"
      : `inset 0 0 0 1px ${DROP_DWELL_LINE}`,
  };
};
